/**
 * Date/Time Formatting Utilities
 *
 * Task: T-P4-D2-F6-FE-3
 *
 * Provides timezone-aware date and time formatting for booking displays.
 * Uses Intl.DateTimeFormat for proper locale and timezone handling.
 *
 * Note: Reuses patterns from calendar.ts but extends for booking-specific formatting.
 */

export interface BookingDateTimeFormatted {
  /** Full date string: "Thursday, December 12, 2024" */
  date: string;
  /** Time range string: "2:00 PM – 3:00 PM EST" */
  timeRange: string;
  /** Timezone abbreviation: "EST", "GMT-5", etc. */
  timezone: string;
}

/**
 * Format a booking's date and time for display in the confirmation summary.
 *
 * @param scheduledAt - ISO 8601 timestamp of booking start
 * @param durationMinutes - Duration in minutes
 * @param timezone - IANA timezone string (e.g., "America/New_York")
 * @returns Formatted date, time range, and timezone abbreviation
 *
 * @example
 * formatBookingDateTime("2024-12-12T14:00:00Z", 60, "America/New_York")
 * // Returns:
 * // {
 * //   date: "Thursday, December 12, 2024",
 * //   timeRange: "2:00 PM – 3:00 PM EST",
 * //   timezone: "EST"
 * // }
 */
export function formatBookingDateTime(
  scheduledAt: string,
  durationMinutes: number,
  timezone: string,
): BookingDateTimeFormatted {
  const startDate = new Date(scheduledAt);
  const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);

  // Format full date: "Thursday, December 12, 2024"
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: timezone,
  });
  const date = dateFormatter.format(startDate);

  // Format start time: "2:00 PM"
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
  });
  const startTime = timeFormatter.format(startDate);
  const endTime = timeFormatter.format(endDate);

  // Get timezone abbreviation: "EST", "GMT-5", etc.
  const tzFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'short',
  });
  const parts = tzFormatter.formatToParts(startDate);
  const tzAbbrev =
    parts.find((part) => part.type === 'timeZoneName')?.value ?? timezone;

  // Use en-dash (–) for range, not hyphen (-)
  const timeRange = `${startTime} – ${endTime} ${tzAbbrev}`;

  return { date, timeRange, timezone: tzAbbrev };
}

// Note: Use formatCurrency from '@/components/payment/formatCurrency' instead
// That version handles null safely and is the canonical currency formatter.

/**
 * Format duration in a human-readable way.
 *
 * @param minutes - Duration in minutes
 * @returns Formatted duration string
 *
 * @example
 * formatDuration(45) // "45 minutes"
 * formatDuration(60) // "1 hour"
 * formatDuration(90) // "1 hr 30 min"
 * formatDuration(120) // "2 hours"
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} minutes`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (remainingMinutes === 0) {
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }

  return `${hours} hr ${remainingMinutes} min`;
}
